import { useId, useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FaPlus, FaMinus } from 'react-icons/fa'
import { fetchContent, subscribeToType, LIVE_MODE } from '../lib/sanity'
import { PortableText } from '@portabletext/react'

interface FAQEntry {
  question: string
  answer: any[]
}

interface FAQContent {
  heading: string
  questions: FAQEntry[]
}

const fallbackQuestions: FAQEntry[] = [
  {
    question: 'What does accountability support include?',
    answer: [{ _type: 'block', children: [{ _type: 'span', text: 'Remote check-ins, routine planning, and practical lifestyle support. Andrea does not diagnose, prescribe, or manage medical treatment.' }] }],
  },
  {
    question: 'Does Andrea replace my healthcare professional?',
    answer: [{ _type: 'block', children: [{ _type: 'span', text: 'No. Andrea provides non-medical support alongside your licensed clinical care team. Medication and health questions belong with that team.' }] }],
  },
  {
    question: 'Can support happen remotely?',
    answer: [{ _type: 'block', children: [{ _type: 'span', text: 'Yes. The accountability and lifestyle support is designed for remote participation.' }] }],
  },
]

const accordionVariants = {
  hidden: { opacity: 0, height: 0 },
  visible: { opacity: 1, height: 'auto' },
}

const FAQRow = ({ question, answer }: FAQEntry) => {
  const [isOpen, setIsOpen] = useState(false)
  const answerId = `faq-answer-${useId().replace(/:/g, '')}`
  const triggerId = `${answerId}-trigger`

  return (
    <div className="rounded-2xl border border-white/80 bg-white/90 p-5 shadow-[0_10px_30px_rgba(6,96,94,0.08)]">
      <h3 className="text-base font-semibold text-slate-900">
        <button
          type="button"
          id={triggerId}
          aria-expanded={isOpen}
          aria-controls={answerId}
          className="flex w-full items-center justify-between text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <span>{question}</span>
          <span className="text-teal-600" aria-hidden="true">{isOpen ? <FaMinus /> : <FaPlus />}</span>
        </button>
      </h3>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={answerId}
            role="region"
            aria-labelledby={triggerId}
            variants={accordionVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="mt-3 text-sm text-slate-600">
              <PortableText value={answer} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const FAQ = () => {
  const [content, setContent] = useState<FAQContent | null>(null)

  useEffect(() => {
    let unsubscribe: (() => void) | undefined
    const getContent = async () => {
      try {
        const [data] = await fetchContent('faq')
        setContent(data)
        if (LIVE_MODE) {
          unsubscribe = subscribeToType('faq', (docs) => setContent(docs[0]))
        }
      } catch (error) {
        console.error('Error fetching FAQ content:', error)
      }
    }
    getContent()
    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [])

  const faqData = content?.questions || fallbackQuestions

  return (
    <section id="faq-section" className="relative isolate overflow-hidden bg-[#cdfbf1] py-24 text-slate-900">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.9),_rgba(205,251,241,0.6))]" />
      <div className="absolute inset-y-0 right-10 w-64 rounded-full bg-white/70 blur-[160px]" />

      <div className="container relative z-10 mx-auto px-6">
        <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] items-start">
          <div className="rounded-3xl border border-white/80 bg-white/90 p-10 shadow-[0_20px_60px_rgba(6,96,94,0.12)]">
            <p className="text-xs uppercase tracking-[0.5em] text-teal-700">Still curious?</p>
            <h2 className="mt-4 text-4xl font-semibold text-slate-900">
              {content?.heading || 'Questions about accountability support'}
            </h2>
            <p className="mt-4 text-sm text-slate-600 leading-relaxed">
              Learn how remote check-ins, practical planning, and lifestyle accountability can fit alongside the guidance you receive from your licensed care team.
            </p>
            <div className="mt-8 rounded-2xl border border-white/80 bg-white/90 p-5 text-sm text-slate-600">
              Prefer to ask something privately? Email <a href="mailto:info@andreavillalobos.com" className="text-teal-700 underline">info@andreavillalobos.com</a> and Andrea will reply within one business day.
            </div>
          </div>

          <div className="space-y-4">
            {faqData.map((item, index) => (
              <FAQRow key={`${item.question}-${index}`} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default FAQ
